import { useQuery } from "@tanstack/react-query";
import { Bug as BugIcon } from "lucide-react";
import { useState } from "react";
import { useUrlFilters } from "../../shared/hooks/urlFilters";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import type {
  BugBoard,
  BugSeverity,
  PageResponse,
  ProjectSummary,
  WorkItemStatus,
  WorkItemSummary,
} from "../../shared/types/api";
import {
  Button,
  Card,
  DataTable,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
  StatusBadge,
  cn,
  type Column,
} from "../../shared/ui";
import { todayLocal } from "../meetings/meetingVocabulary";
import {
  ArchiveGroupField,
  reopenArchived,
  reopenColumn,
  useArchiveGroups,
  useArchivedItems,
} from "../workitems/archiveGroups";
import { BulkMoveBar } from "../workitems/BulkMoveBar";
import { WorkItemFormDrawer } from "../workitems/WorkItemFormDrawer";
import {
  PRIORITY_LABELS,
  PRIORITY_TONE,
  SEVERITY_LABELS,
  SEVERITY_TONE,
  STATUS_LABELS,
  STATUS_TONE,
} from "../workitems/workItemVocabulary";
import { SEVERITY_ORDER, daysOpen, formatAge, isOpen } from "./bugVocabulary";

/** Ten a page to begin with, and the reader can ask for more. The order and the counts are the
 * server's, because a page cannot work them out. */
const DEFAULT_PAGE_SIZE = "10";

/**
 * Every bug the testing team has to keep track of, worst first.
 *
 * <p>One component behind two doors — a project's Bugs tab and the organisation-wide Bugs page —
 * because a tester covering three projects and a manager watching one are asking the same
 * question of the same rows, and two screens that drifted apart would answer it differently.
 *
 * <p>Severity decides the order and the colour; priority sits beside it as its own column. How
 * bad a fault is and how soon it will be picked up are different questions, and collapsing them
 * leaves nobody able to say "serious, but it can wait for the next release".
 */
/* The parameters this list owns. Outside the component so its identity is stable — useUrlFilters
   memoises on it. Open bugs only by default, which is what a tester wants on arrival. */
const BUG_FILTERS = {
  q: "", severity: "", status: "", openOnly: "true", project: "", page: "0",
  size: DEFAULT_PAGE_SIZE, raisedFrom: "", raisedTo: "", group: "",
};

export function BugList({ projectId }: { projectId?: string }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const today = todayLocal();

  /* In the URL, so opening a bug and coming back returns to the list as it was left. */
  const [filters, setFilters] = useUrlFilters(BUG_FILTERS);
  const severity = filters.severity as BugSeverity | "";
  const status = filters.status as WorkItemStatus | "";
  const openOnly = filters.openOnly !== "false";
  const { q: search, raisedFrom, raisedTo, group } = filters;
  /* Reading a group replaces the board rather than narrowing it: its rows are precisely the ones
     every other filter here is built to leave out. */
  const viewingGroup = group !== "";
  const page = Number(filters.page) || 0;
  /* How many rows the reader asked for, kept in the URL beside the filters so coming back from
     a bug returns the list the size they left it. */
  const pageSize = Number(filters.size) || Number(DEFAULT_PAGE_SIZE);
  /* Only the organisation-wide page offers this; inside a project the answer is already fixed. */
  const chosenProject = filters.project;

  const [raising, setRaising] = useState(false);
  const [reopening, setReopening] = useState<string | null>(null);
  /* Cleared whenever the rows underneath change. A selection that survived a filter would act on
     bugs the tester can no longer see, which is the one thing a batch must never do. */
  const [selected, setSelected] = useState<string[]>([]);
  const scopedTo = projectId ?? (chosenProject === "" ? undefined : chosenProject);

  /* Every filter returns to the first page. Page three of a wider result is empty under a
     narrower one, and an empty table reads as "nothing matches" rather than "wrong page". */
  const refilter = (patch: Partial<typeof BUG_FILTERS>) => {
    setFilters({ ...patch, page: "0" });
    setSelected([]);
  };

  const projects = useQuery({
    queryKey: ["projects", "picker"],
    queryFn: () =>
      api.get<PageResponse<ProjectSummary>>("/api/v1/projects?size=200&sort=name,asc"),
    enabled: !projectId,
  });

  const query = useQuery({
    queryKey: [
      "bugs", scopedTo ?? "all", severity, status, openOnly, search, raisedFrom, raisedTo,
      page, pageSize,
    ],
    queryFn: () => {
      const params = new URLSearchParams({
        openOnly: String(openOnly),
        page: String(page),
        size: String(pageSize),
      });
      if (scopedTo) {
        params.set("projectId", scopedTo);
      }
      if (severity) {
        params.set("severity", severity);
      }
      if (status) {
        params.set("status", status);
      }
      if (search.trim()) {
        params.set("q", search.trim());
      }
      /* When it was raised — the date "Open for" counts from. Either end stands on its own. */
      if (raisedFrom) {
        params.set("raisedFrom", raisedFrom);
      }
      if (raisedTo) {
        params.set("raisedTo", raisedTo);
      }
      return api.get<BugBoard>(`/api/v1/work-items/bugs?${params.toString()}`);
    },
  });

  /*
   * Both come from the server. Ordering ten rows in the browser would sort a page among itself
   * and call the result the worst thing outstanding, and counting them would describe the page
   * rather than the backlog — which is the opposite of what the strip is for.
   */
  const groups = useArchiveGroups(projectId);
  const archived = useArchivedItems(group);

  const reopen = (itemId: string) => {
    setReopening(itemId);
    void (async () => {
      try {
        await reopenArchived(itemId);
        /* Both: the bug leaves the group, and the count beside the group's name moves with it. */
        await Promise.all([archived.refetch(), groups.refetch(), query.refetch()]);
      } finally {
        setReopening(null);
      }
    })();
  };

  const rows = viewingGroup ? (archived.data ?? []) : (query.data?.page.content ?? []);
  const counts = query.data?.counts;

  if (query.isLoading) {
    return <LoadingState />;
  }
  if (query.error) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const allShownSelected = rows.length > 0 && rows.every((row) => selected.includes(row.id));

  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((one) => one !== id) : [...current, id],
    );

  const columns: Column<WorkItemSummary>[] = [
    /* No ticking while a group is open: every batch action acts on live work, and these rows
       have already left it. */
    ...(can("WORK_ITEM_EDIT") && !viewingGroup
      ? [
          {
            key: "select",
            header: (
              <input
                type="checkbox"
                aria-label="Select every bug on this page"
                checked={allShownSelected}
                onChange={() =>
                  setSelected(allShownSelected ? [] : rows.map((row) => row.id))
                }
                className="h-4 w-4 rounded border-ink-300"
              />
            ),
            className: "w-10",
            render: (bug: WorkItemSummary) => (
              <input
                type="checkbox"
                aria-label={`Select ${bug.itemKey}`}
                checked={selected.includes(bug.id)}
                /* The row itself opens the bug, so the tick must not also do that. */
                onClick={(event) => event.stopPropagation()}
                onChange={() => toggle(bug.id)}
                className="h-4 w-4 rounded border-ink-300"
              />
            ),
          },
        ]
      : []),
    {
      key: "severity",
      header: "Severity",
      render: (bug) =>
        bug.severity ? (
          <StatusBadge tone={SEVERITY_TONE[bug.severity]}>
            {SEVERITY_LABELS[bug.severity]}
          </StatusBadge>
        ) : (
          <span className="text-ink-400">—</span>
        ),
    },
    {
      key: "bug",
      header: "Bug",
      render: (bug) => (
        <div>
          <div className="text-sm font-medium text-ink-900">{bug.title}</div>
          <div className="text-xs text-ink-500">
            {bug.itemKey}
          </div>
        </div>
      ),
    },
    ...(projectId
      ? []
      : [
          {
            key: "project",
            header: "Project",
            render: (bug: WorkItemSummary) => (
              <span className="text-sm text-ink-700">{bug.projectName ?? bug.projectKey}</span>
            ),
          },
        ]),
    {
      key: "status",
      header: "Status",
      render: (bug) => (
        <StatusBadge tone={STATUS_TONE[bug.status]}>{STATUS_LABELS[bug.status]}</StatusBadge>
      ),
    },
    {
      key: "priority",
      header: "Priority",
      render: (bug) => (
        <StatusBadge tone={PRIORITY_TONE[bug.priority]}>
          {PRIORITY_LABELS[bug.priority]}
        </StatusBadge>
      ),
    },
    {
      key: "assignee",
      header: "Assignee",
      render: (bug) =>
        bug.assigneeName ?? <span className="text-ink-400">Unassigned</span>,
    },
    {
      key: "age",
      header: "Open for",
      render: (bug) => {
        const days = isOpen(bug) ? daysOpen(bug.detectedDate, today) : null;
        return (
          <span className={cn("tnum text-sm", days != null && days >= 14 && "text-red-600")}>
            {isOpen(bug) ? formatAge(days) : "closed"}
          </span>
        );
      },
    },
    {
      key: "blocked",
      header: "Blocked",
      render: (bug) =>
        bug.openBlockerCount > 0 ? (
          <StatusBadge tone="red">{bug.openBlockerCount}</StatusBadge>
        ) : (
          <span className="text-ink-400">—</span>
        ),
    },
    ...(viewingGroup && can("WORK_ITEM_EDIT") ? [reopenColumn(reopen, reopening)] : []),
  ];

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          {/* One row of controls: the search takes the slack, every chooser is the same width,
              and the button is held to the far end — the same shape the work list uses, so the
              two tabs do not look like different applications. */}
          <div className="flex flex-1 flex-wrap items-end gap-3 lg:flex-nowrap">
            <div className="min-w-36 flex-1">
              <Field label="Search">
                <Input
                  placeholder="Bug or key"
                  value={search}
                  onChange={(event) => refilter({ q: event.target.value })}
                />
              </Field>
            </div>
            {!projectId && (
              <div className="w-32 shrink-0">
                <Field label="Project">
                  <Select
                    value={chosenProject}
                    onChange={(event) => refilter({ project: event.target.value })}
                  >
                    <option value="">Every project</option>
                    {(projects.data?.content ?? []).map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
            )}
            <div className="w-32 shrink-0">
              <Field label="Severity">
                <Select
                  value={severity}
                  onChange={(event) => refilter({ severity: event.target.value })}
                >
                  <option value="">Any severity</option>
                  {SEVERITY_ORDER.map((value) => (
                    <option key={value} value={value}>
                      {SEVERITY_LABELS[value]}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <div className="w-32 shrink-0">
              <Field label="Status">
                <Select
                  value={status}
                  onChange={(event) => refilter({ status: event.target.value })}
                >
                  <option value="">Any status</option>
                  {(Object.keys(STATUS_LABELS) as WorkItemStatus[]).map((value) => (
                    <option key={value} value={value}>
                      {STATUS_LABELS[value]}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <div className="shrink-0">
              <Field label="Raised between">
                <div className="flex items-center gap-1.5">
                  <Input
                    type="date"
                    aria-label="Raised from"
                    className="w-[8.5rem]"
                    value={raisedFrom}
                    max={raisedTo || undefined}
                    onChange={(event) => refilter({ raisedFrom: event.target.value })}
                  />
                  <span className="text-sm text-ink-400">–</span>
                  <Input
                    type="date"
                    aria-label="Raised to"
                    className="w-[8.5rem]"
                    value={raisedTo}
                    min={raisedFrom || undefined}
                    onChange={(event) => refilter({ raisedTo: event.target.value })}
                  />
                </div>
              </Field>
            </div>
            <ArchiveGroupField
              groups={groups.data ?? []}
              value={group}
              /* Its own reset rather than refilter's: the page number belongs to the live board,
                 and a group is read whole. */
              onChange={(next) => {
                setFilters({ group: next, page: "0" });
                setSelected([]);
              }}
            />
            <label className="flex shrink-0 items-center gap-2 pb-2 text-sm text-ink-700">
              <input
                type="checkbox"
                checked={openOnly}
                onChange={(event) => refilter({ openOnly: String(event.target.checked) })}
                className="h-4 w-4 rounded border-ink-300"
              />
              Open bugs only
            </label>
          </div>
          {can("WORK_ITEM_CREATE") && projectId && (
            <Button
              className="shrink-0"
              icon={<BugIcon className="h-4 w-4" />}
              onClick={() => setRaising(true)}
            >
              Raise a bug
            </Button>
          )}
        </div>

        {/* The question a tester opens this screen to ask, answered before they read a single
            row. Each count is a filter, so "two blockers" becomes those two bugs in one click.
            Hidden while a group is open: the counts describe the live board, and showing them
            above a table of archived rows would have them describing something else. */}
        {!viewingGroup && (
        <div className="mt-4 flex flex-wrap gap-2">
          {SEVERITY_ORDER.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => refilter({ severity: severity === value ? "" : value })}
              className={cn(
                "rounded-lg border px-3 py-1.5 text-sm transition-colors",
                severity === value
                  ? "border-brand-500 bg-brand-50 text-brand-800"
                  : "border-ink-200 text-ink-600 hover:border-ink-300",
                (counts?.[value] ?? 0) === 0 && "opacity-50",
              )}
            >
              <span className="tnum font-medium">{counts?.[value] ?? 0}</span>{" "}
              {SEVERITY_LABELS[value]}
            </button>
          ))}
        </div>
        )}
      </Card>

      {!viewingGroup && (
      <BulkMoveBar
        items={rows.filter((row) => selected.includes(row.id))}
        onClear={() => setSelected([])}
        onDone={(allMoved) => {
          if (allMoved) {
            setSelected([]);
          }
          void query.refetch();
        }}
      />
      )}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(bug) => bug.id}
        loading={viewingGroup ? archived.isPending : false}
        /* A group is read whole, so it carries no paging controls — omitting onPageChange is
           what hides them. */
        page={viewingGroup ? 0 : page}
        pageSize={viewingGroup ? rows.length || 1 : pageSize}
        totalElements={viewingGroup ? rows.length : (query.data?.page.totalElements ?? 0)}
        onPageChange={viewingGroup ? undefined : (next) => {
          setFilters({ page: String(next) });
          setSelected([]);
        }}
        onPageSizeChange={viewingGroup ? undefined : (next) => {
          setFilters({ size: String(next), page: "0" });
          setSelected([]);
        }}
        onRowClick={(bug) => navigate(`/work-items/${bug.itemKey}`)}
        /* A search that matches nothing is not an empty backlog, and saying so would tell the
           reader the wrong thing about their project. */
        emptyTitle={
          viewingGroup
            ? "This group is empty"
            : search.trim()
              ? "Nothing matches that search"
              : openOnly
                ? "No open bugs"
                : "No bugs"
        }
        emptyMessage={
          viewingGroup
            ? "Everything that was in it has been reopened."
            : search.trim()
              ? "No bug here has that in its title or its key."
              : openOnly
                ? "Nothing outstanding here. Untick “Open bugs only” to see the ones already closed."
                : "Nothing has been raised against this yet."
        }
      />

      {projectId && (
        <WorkItemFormDrawer
          projectId={projectId}
          /* The button says "Raise a bug", so the form does not start on Story and wait to be
             corrected. */
          fixedType="BUG"
          open={raising}
          onClose={() => setRaising(false)}
          onSaved={() => {
            setRaising(false);
            void query.refetch();
          }}
        />
      )}
    </div>
  );
}
